import {ChevronLeft, ChevronRight} from "@material-ui/icons";
import {Button, IconButton} from "@zendeskgarden/react-buttons";
import {DrawerModal} from "@zendeskgarden/react-modals";
import {Title, Well} from "@zendeskgarden/react-notifications";
import {ThemeProvider} from "@zendeskgarden/react-theming";
import {ReactComponent as HamburgerMenu} from '@zendeskgarden/svg-icons/src/16/menu-stroke.svg';
import {ReactElement, useContext, useEffect, useRef, useState} from "react";
import {BiCollapse, BiExpand} from "react-icons/bi";
import {CgChevronLeft} from "react-icons/cg";
import {useHistory, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import {useGetPropertyWorkflowLazyQuery} from "../../__generated__/generated_types";
import {AuthContext} from "../../contexts/AuthContext";
import {useProperties} from "../../contexts/properties/PropertiesContext";
import {showWorkflows} from "../../global/feature_checkers";
import {ViziblyTheme} from "../../pages/analyst/ViziblyZDGTheme";
import BarLoader from "../bar-loader/BarLoader";
import {useUserWorkflow} from "./logic/useUserWorkflow";
import css from "./styles/user-workflows.module.scss";
import {UserWorkflowOverview} from "./UserWorkflowOverview";
import {UserWorkflowPageDecider} from './UserWorkflowPageDecider';
import {UserWorkflowStatusButton} from "./UserWorkflowStatusButton";


export function UserWorkflow(): ReactElement {
    const history = useHistory();
    const { user } = useContext(AuthContext);
    const {currentProperty} = useProperties();
    const {userWorkflowTaskId, type} = useParams<{
        userWorkflowTaskId: string,
        type: string,
    }>();
    const {
        enterWorkflow,
        exitWorkflow,
        nextTask,
        previousTask,
        activeWorkflow,
        activeTask,
        updateTaskStatus,
    } = useUserWorkflow();

    const [tableOfContentsOpen, setTableOfContentsOpen] = useState(false);
    const instructionsContainer = useRef<HTMLDivElement>(null);

    const [getWorkflow, {data, loading}] = useGetPropertyWorkflowLazyQuery({
        fetchPolicy: "network-only"
    });

    useEffect(() => {
        if (user && !(showWorkflows(user) || user.isInternalUser)) {
            history.push("/setup/start");
            toast.warning("Workflows have not been enabled.");
        }

        if (activeWorkflow) {
            const requestedTaskInCurrentWorkflow = activeWorkflow.tasks.find(task => task.id == userWorkflowTaskId);
            setTableOfContentsOpen(false);
            instructionsContainer.current?.classList.remove(css.isHidden);

            // workflow for task already loaded, no need to fetch again
            if (requestedTaskInCurrentWorkflow) {
                return;
            }
        }

        if (userWorkflowTaskId && currentProperty) {
            getWorkflow({
                variables: {
                    taskId: userWorkflowTaskId,
                    propertyId: currentProperty.id,
                    budgetYear: currentProperty.budgetYear,
                }
            });
        }
    }, [currentProperty, userWorkflowTaskId, user]);

    useEffect(() => {
        const workflow = data?.getPropertyWorkflow;
        const task = workflow?.tasks.find(x => x.id == userWorkflowTaskId);

        if (workflow && task) {
            // @bowman - this is a temporary fix for the UI sometimes loading a stale workflow instance, causing
            // the status of tasks to appear as whatever they were on initial load from the backend. I'd like to revisit
            // the whole workflow navigation logic, and remove this weirdness
            enterWorkflow(activeWorkflow ?? workflow, task.id);
        }
    }, [data, userWorkflowTaskId]);

    const previousButtonDisabled = loading || !activeTask || activeTask.position == 0;
    const nextButtonDisabled = loading || !activeTask || !activeWorkflow || activeTask.position == activeWorkflow.tasks.length - 1;

    function progressPercentage(): number {
        const totalTasks = activeWorkflow?.tasks.length;
        const currentTaskPosition = activeTask?.position;

        if (totalTasks != null && currentTaskPosition != null) {
            return ((currentTaskPosition + 1) / totalTasks) * 100;
        }

        return 0;
    }

    return (
        <ThemeProvider theme={ViziblyTheme} focusVisibleRef={null}>
            <div className={css.userWorkflow}>
                <div className={css.uwNavBar}>
                    <div className={css.uwNavBarInfo}>
                        <IconButton isPrimary aria-label="workflow overview toggle" onClick={() => setTableOfContentsOpen(true)}>
                            <HamburgerMenu />
                        </IconButton>

                        {(activeTask?.position ?? 0) + 1}/{activeWorkflow?.tasks.length}
                        {/* <h4 className={css.uwNavBarTitle}>{activeWorkflow?.title}</h4>
                        <h4 className={css.uwNavBarTaskTitle}>{activeTask?.title}</h4> */}
                        <h4 className={css.uwNavBarTitle} style={{marginRight: "auto"}}>{activeTask?.title}</h4>

                        <UserWorkflowStatusButton
                            task={activeTask}
                            property={currentProperty}
                            onTaskUpdate={statusUpdate => {
                                if (statusUpdate && activeTask) {
                                    updateTaskStatus(activeTask.id, statusUpdate);
                                } else {
                                    toast.error("There was a problem updating the Task status.");
                                }
                            }}
                        />

                        <Button
                            onClick={previousTask}
                            isPrimary
                            disabled={previousButtonDisabled}
                        >
                            <Button.StartIcon>
                                <ChevronLeft />
                            </Button.StartIcon>
                            Previous Task
                        </Button>

                        <Button
                            onClick={nextTask}
                            isPrimary
                            disabled={nextButtonDisabled}
                        >
                            Next Task
                            <Button.EndIcon>
                                <ChevronRight />
                            </Button.EndIcon>
                        </Button>

                        <Button onClick={exitWorkflow} isBasic>Exit Workflow</Button>
                    </div>
                    <div className={css.uwProgressBar}>
                        <div className={css.uwProgressBarInner} style={{width: `${progressPercentage()}%`}}></div>
                    </div>
                </div>

                <div id="uw-content-container" className={css.uwContent}>
                    <BarLoader loading={loading} />
                    { activeTask && currentProperty &&
                        <UserWorkflowPageDecider task={activeTask} versionType={type} property={currentProperty} />
                    }
                </div>

                { activeTask?.instructions &&
                    <Well isFloating className={css.uwInstructions} ref={instructionsContainer}>
                        <Title>Task Instructions</Title>
                        <div className={css.uwInstructionsBody}>
                            <p>
                                {activeTask.instructions}
                            </p>
                        </div>

                        <IconButton
                            className={css.expandButton}
                            onClick={() => instructionsContainer.current?.classList.toggle(css.isExpanded)}
                        >
                            <>
                                <BiExpand className={css.expandButtonIcon} />
                                <BiCollapse className={css.collapseButtonIcon} />
                            </>
                        </IconButton>

                        <IconButton
                            className={css.hideButton}
                            onClick={() => instructionsContainer.current?.classList.toggle(css.isHidden)}
                        >
                            <CgChevronLeft />
                        </IconButton>
                    </Well>
                }

                <DrawerModal
                    isOpen={tableOfContentsOpen}
                    onClose={() => setTableOfContentsOpen(false)}
                    className={css.uwTableOfContents}
                >
                    <DrawerModal.Header>
                        Workflow Overview
                    </DrawerModal.Header>

                    <DrawerModal.Body className={css.uwContentsBody}>
                        { activeWorkflow && activeTask &&
                            <UserWorkflowOverview
                                workflow={activeWorkflow}
                                activeTask={activeTask}
                                visibilityChanged={tableOfContentsOpen}
                            />
                        }
                    </DrawerModal.Body>

                    <DrawerModal.Close />
                </DrawerModal>
            </div>
        </ThemeProvider>
    );
}
